import React, { useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { uniq } from 'lodash';
import { differenceInDays, parseISO } from 'date-fns';
import { LOGGEDIN_LINKS } from '@web/molecules';
import {
  Footer,
  LoadingView,
  SplitPanels,
  WelcomeToThePack,
} from '@web/components';
import { ConfirmationHero } from '../ConfirmationHero';
import { AccountFeatures } from '../AccountFeatures';
import { ConfirmationOrderSummary } from '../ConfirmationOrderSummary';
import { usePets } from '../../pets';
import { useSubscriptions } from '../../subscriptions';
import { useProductsList } from '../../products';
import { useSubscriptionAddonProductsList } from '../../addons';
import { useReporter } from '../../reporter';
import { useSession } from '../../api';
import { useOrders, useOrderList } from '../../orders';
import { useAddress, useSessionCustomer } from '../../customer';
import { usePayment } from '../../payment';
import { AccountHeader } from '../../account/AccountHeader';
import { FOOTER_CONTENT } from '../../testing/constants';
import { RedirectToDashboard } from '../routing';
import { OnboardingContext } from '../OnboardingFlowContainer/context';
import {
  calculateGrandTotals,
  formatDate,
  formatDeliveryAddress,
  formatOrderSummary,
  getConfirmationOrderSummaryProps,
  formatPets,
} from './dataParsers';
import {
  userViewsCheckoutConfirmation,
  userClickedViewAccount,
} from './events';
import {
  StyledCheckoutConfirmationContainer,
  Container,
  AccountFeaturesContainer,
  ConfirmationOrderSummaryContainer,
  ConfirmationHeroContainer,
  SplitPanelsContainer,
} from './appStyles';

export const AppConfirmationContainer = ({
  headline,
  description,
  viewAccountURL,
  buttonText,
  features,
  splitPanels,
  orderSummaryTitle,
  pricingList,
}) => {
  const { offer } = useContext(OnboardingContext);
  const pricePeriod = 'week';
  const { user } = useSession();
  const { customer, isLoading: isCustomerLoading } = useSessionCustomer();
  const { subscriptions, isLoading: isSubscriptionsLoading } =
    useSubscriptions();
  const { pets, isLoading: isPetsLoading } = usePets();
  const { products, isLoading: isProductsLoading } = useProductsList(
    !isSubscriptionsLoading
      ? uniq(subscriptions.map(subscription => subscription.sku))
      : null,
  );

  const { data: addons, isLoading: isAddonsLoading } =
    useSubscriptionAddonProductsList();
  const { orders, isLoading: isOrdersLoading } = useOrders();
  const orderIds = orders?.map(order => order.id);
  const { orders: orderList, isLoading: isOrderListLoading } =
    useOrderList(orderIds);
  const { address, isLoading: isAddressLoading } = useAddress(customer?.id);
  const { payment, isLoading: isPaymentLoading } = usePayment(customer?.id);
  const isLoading =
    isSubscriptionsLoading ||
    isPetsLoading ||
    isProductsLoading ||
    isAddonsLoading ||
    isOrdersLoading ||
    isOrderListLoading ||
    isAddressLoading ||
    isCustomerLoading ||
    isPaymentLoading;
  const reporter = useReporter();

  useEffect(() => {
    if (!isLoading) {
      reporter.tag(userViewsCheckoutConfirmation());
    }
  }, [reporter, isLoading]);

  if (isLoading) {
    return <LoadingView />;
  }

  const firstOrderDate = () => {
    return (
      orders?.reduce((oldest, order) => {
        return oldest < order.date_created ? oldest : order.date_created;
      }, orders[0].date_created) ?? null
    );
  };

  // Redirect to dashboard if order is >= 1 day ago
  if (
    !isLoading &&
    differenceInDays(new Date(), parseISO(firstOrderDate())) >= 1
  ) {
    return (
      <RedirectToDashboard pet={pets[0]}>
        <LoadingView />
      </RedirectToDashboard>
    );
  }

  const ordersSummary = formatOrderSummary(
    pets,
    subscriptions,
    orderList,
    products,
    addons,
    pricePeriod,
  );

  // TODO: Get this from the backend (both price total and after discount)
  const { subtotal, totalTax, totalPrice } = calculateGrandTotals(orderList);
  const deliveryDate = formatDate(orderList[0].delivery_date);
  const confirmationHeroProps = {
    customerName: customer.first_name,
    deliveryDate: deliveryDate,
    pets: formatPets(pets),
  };

  const { formattedPricingList, plans } = getConfirmationOrderSummaryProps(
    pricingList,
    offer,
    orderList,
    pets,
    products,
  );

  const confirmationOrderSummaryProps = {
    orders: ordersSummary,
    deliveryAddress: formatDeliveryAddress(address),
    cardNumberLastFour: payment.last_four_digits,
    wallet: payment.wallet,
    cardBrand: payment.brand,
    deliveryDate: deliveryDate,
    orderTotalCost: totalPrice,
    orderTotalTax: totalTax,
    orderSubtotal: subtotal,
    orderSummaryTitle,
    period: pricePeriod,
    pricingList: formattedPricingList,
    plans: plans,
  };
  const accountFeatureProps = {
    headline,
    description,
    viewAccountURL,
    buttonText,
    features,
  };

  return (
    <StyledCheckoutConfirmationContainer>
      <AccountHeader
        links={LOGGEDIN_LINKS}
        logoUrl={'/'}
        logoSrText={'Ollie Logo'}
        name={customer.first_name}
      />
      <Container>
        <ConfirmationHeroContainer>
          <ConfirmationHero {...confirmationHeroProps} />
        </ConfirmationHeroContainer>
        <ConfirmationOrderSummaryContainer>
          <WelcomeToThePack accessToken={user.access_token} />
          <ConfirmationOrderSummary {...confirmationOrderSummaryProps} />
        </ConfirmationOrderSummaryContainer>
        <AccountFeaturesContainer>
          <AccountFeatures
            {...accountFeatureProps}
            onButtonClick={() => reporter.tag(userClickedViewAccount())}
          />
        </AccountFeaturesContainer>
        <SplitPanelsContainer>
          {splitPanels.map((item, i) => (
            <SplitPanels key={i} {...item} />
          ))}
        </SplitPanelsContainer>
      </Container>
      <Footer {...FOOTER_CONTENT} />
    </StyledCheckoutConfirmationContainer>
  );
};

AppConfirmationContainer.displayName = 'AppConfirmation';
AppConfirmationContainer.propTypes = {
  headline: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  features: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
    }),
  ).isRequired,
  buttonText: PropTypes.string.isRequired,
  viewAccountURL: PropTypes.string.isRequired,
  splitPanels: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
  orderSummaryTitle: PropTypes.string.isRequired,
  pricingList: PropTypes.shape({
    puptainerLabel: PropTypes.string.isRequired,
    shippingLabel: PropTypes.string.isRequired,
    scooperLabel: PropTypes.string.isRequired,
    bowlLabel: PropTypes.string,
    freeItems: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
      }),
    ),
  }),
};
